import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faRocket } from '@fortawesome/pro-light-svg-icons/faRocket';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useStreamingAccelerator } from '../../hooks/useStreamingAccelerator';
import { acceleratorSummary, acceleratorView } from '../../periodSections';
import { acceleratorMessages as messages } from '../../acceleratorMessages';
import { streamingPeriodMessages } from '../../streamingPeriodMessages';
import { StreamingPlanLockCard } from '../../parts/StreamingPlanLockCard';
import { SectionMonthLine } from '../../period/SectionMonthLine';
import { SectionNoneCard } from '../../period/SectionNoneCard';
import { AcceleratorTiles } from './AcceleratorTiles';
import { AcceleratorMonthBars } from './AcceleratorMonthBars';
import { AcceleratorTracksTable } from './AcceleratorTracksTable';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** TuneCore's own site, where the artist turns Accelerator on. */
export const TUNECORE_URL = 'https://web.tunecore.com';

interface AcceleratorBodyProps {
  accountId: number;
  days: number;
}

/** The read of Accelerator: the tiles, the month bars and the tracks of the newest month. */
const AcceleratorBodyComponent: React.FC<AcceleratorBodyProps> = ({ accountId, days }) => {
  const intl = useIntl();
  const { data, isLoading, error, retry } = useStreamingAccelerator({ accountId, days, skip: false });
  const summary = useMemo(() => acceleratorSummary(data?.byMonth ?? []), [data]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (data === undefined) {
    return null;
  }
  if (data.locked === 'plan') {
    return (
      <StreamingPlanLockCard
        feature={SMLUpgradeFeature.streamingSplit}
        title={streamingPeriodMessages.planTitle}
        testId="streaming-accelerator-plan-lock"
      />
    );
  }

  const view = acceleratorView(data);

  return (
    <div className={shared.page} data-testid="streaming-section-accelerator">
      <SectionMonthLine text={intl.formatMessage(messages.monthLine)} months={data.months} />
      {!data.available ? (
        <p className={shared.faint}>{intl.formatMessage(streamingPeriodMessages.notAvailable)}</p>
      ) : null}
      {view === 'noData' ? (
        <SectionNoneCard
          icon={faRocket}
          testId="streaming-accelerator-noData"
          title={intl.formatMessage(messages.noDataTitle)}
          text={intl.formatMessage(messages.noDataText)}
        />
      ) : null}
      {view === 'off' ? (
        <SectionNoneCard
          icon={faRocket}
          testId="streaming-accelerator-off"
          title={intl.formatMessage(messages.offTitle)}
          text={intl.formatMessage(messages.offText)}
        >
          <SMLButton href={TUNECORE_URL}>{intl.formatMessage(messages.openTuneCore)}</SMLButton>
        </SectionNoneCard>
      ) : null}
      {view === 'numbers' ? (
        <>
          <AcceleratorTiles summary={summary} trackCount={data.tracks.length} />
          <AcceleratorMonthBars byMonth={data.byMonth} />
          {data.tracks.length > 0 && data.tracksMonth !== null ? (
            <AcceleratorTracksTable tracks={data.tracks} month={data.tracksMonth} />
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export const AcceleratorBody = TypedMemo(AcceleratorBodyComponent);
